import React, { useCallback, useEffect, useState } from 'react';
import './Login.css';
import { getHttpURL } from '../config/api';

function Login({ onAuthSuccess }) {
  const [authError, setAuthError] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResetMode, setIsResetMode] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState(null);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [signupForm, setSignupForm] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    password: '',
    retypePassword: '',
  });
  const [signupError, setSignupError] = useState(null);
  const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);

  const shouldRequirePasswordReset = useCallback((data) => {
    const user = data?.user || {};
    return Boolean(
      data?.requires_password_reset ||
      data?.password_reset_required ||
      data?.must_reset_password ||
      user?.requires_password_reset ||
      user?.password_reset_required ||
      user?.must_reset_password ||
      user?.is_temporary_password
    );
  }, []);

  const authenticateWithProvider = useCallback(
    async (endpoint, payload, fallbackErrorMessage) => {
      const backendResponse = await fetch(getHttpURL(endpoint), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await backendResponse.json().catch(() => ({}));
      if (backendResponse.ok && data.success) {
        setAuthError(null);
        setResetError(null);
        onAuthSuccess(data.user);
        return true;
      }

      setAuthError(data.detail || data.message || fallbackErrorMessage);
      return false;
    },
    [onAuthSuccess]
  );

  const handleCredentialResponse = useCallback(
    async (response) => {
      try {
        await authenticateWithProvider(
          'auth/google',
          { credential: response.credential },
          'Error logging in. Please try again.'
        );
      } catch (error) {
        console.error('Authentication error:', error);
        setAuthError('Error logging in. Please try again.');
      }
    },
    [authenticateWithProvider]
  );

  useEffect(() => {
    if (isSignupMode || isResetMode) return undefined;

    // Load Google Identity Services script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    script.onload = () => {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      
      if (!clientId) {
        console.error('REACT_APP_GOOGLE_CLIENT_ID environment variable is required');
        setAuthError('Google OAuth configuration missing. Please check environment variables.');
        return;
      }
      
      console.log('Google OAuth Client ID:', clientId);
      
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
      });

      const googleButton = document.getElementById('google-signin-button');
      if (googleButton) {
        window.google.accounts.id.renderButton(
          googleButton,
          {
            theme: 'outline',
            size: 'large',
            shape: 'rectangular',
            width: 340,
            text: 'continue_with',
            logo_alignment: 'center',
          }
        );
      }
    };

    return () => {
      // Clean up script when component unmounts
      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, [handleCredentialResponse, isResetMode, isSignupMode]);

  const updateSignupField = (field) => (e) => {
    setSignupForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setAuthError(null);
    setResetError(null);
    setIsSubmitting(true);
    try {
      const backendResponse = await fetch(getHttpURL('auth/email'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });

      const data = await backendResponse.json().catch(() => ({}));
      if (backendResponse.ok && data.success) {
        if (shouldRequirePasswordReset(data)) {
          setIsResetMode(true);
          setResetEmail((data?.user?.email || email || '').trim());
          setResetToken(String(data?.reset_token || data?.password_reset_token || ''));
          setNewPassword('');
          setConfirmPassword('');
          setAuthError(null);
          return;
        }
        onAuthSuccess(data.user);
        return;
      }
      setAuthError(data.detail || data.message || 'Invalid email or password.');
    } catch (error) {
      console.error('Email authentication error:', error);
      setAuthError('Error logging in. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetError(null);

    const trimmedEmail = (resetEmail || email || '').trim();
    if (!trimmedEmail) {
      setResetError('Email is required.');
      return;
    }
    if (!newPassword || !confirmPassword) {
      setResetError('Please fill out both password fields.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setResetError('Use at least 8 characters for your new password.');
      return;
    }

    setIsResetSubmitting(true);
    try {
      const payload = {
        email: trimmedEmail,
        new_password: newPassword,
        password: newPassword,
      };
      if (resetToken) {
        payload.reset_token = resetToken;
      }

      const backendResponse = await fetch(getHttpURL('auth/password/reset'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await backendResponse.json().catch(() => ({}));
      if (backendResponse.ok && data.success) {
        setResetError(null);
        onAuthSuccess(data.user);
        return;
      }

      setResetError(data.detail || data.message || 'Unable to reset password. Please try again.');
    } catch (error) {
      console.error('Password reset error:', error);
      setResetError('Unable to reset password. Please try again.');
    } finally {
      setIsResetSubmitting(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError(null);
    setAuthError(null);
    setResetError(null);

    if (signupForm.password !== signupForm.retypePassword) {
      setSignupError('Passwords do not match.');
      return;
    }
    if (signupForm.password.length < 8) {
      setSignupError('Use at least 8 characters for your password.');
      return;
    }

    setIsSignupSubmitting(true);
    try {
      const backendResponse = await fetch(getHttpURL('auth/signup'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          first_name: signupForm.firstName,
          last_name: signupForm.lastName,
          company: signupForm.company,
          email: signupForm.email,
          password: signupForm.password,
          retype_password: signupForm.retypePassword,
        }),
      });

      const data = await backendResponse.json().catch(() => ({}));
      if (backendResponse.ok && data.success) {
        setSignupError(null);
        onAuthSuccess(data.user);
        return;
      }

      setSignupError(data.detail || data.message || 'Unable to create account. Please try again.');
    } catch (error) {
      console.error('Signup error:', error);
      setSignupError('Unable to create account. Please try again.');
    } finally {
      setIsSignupSubmitting(false);
    }
  };

  const handleBackToSignIn = () => {
    setIsResetMode(false);
    setIsSignupMode(false);
    setResetError(null);
    setSignupError(null);
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handleOpenSignup = () => {
    setIsSignupMode(true);
    setIsResetMode(false);
    setAuthError(null);
    setResetError(null);
    setSignupError(null);
  };

  return (
    <div className="trouve-replica login-overlay">
      <div className="login-card">
        <div className="auth-brand-mark" aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none">
            <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" stroke="currentColor" strokeWidth="1.5" />
            <path d="M5 6h6M8 6v5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </div>
        <h1 className="auth-brand">Trouve AI</h1>

        {isSignupMode ? (
          <>
            <p className="auth-subtitle">Create your account</p>

            <form onSubmit={handleSignup} className="auth-form signup-form">
              <div className="auth-field-row">
                <input
                  type="text"
                  placeholder="First Name"
                  className="auth-input"
                  value={signupForm.firstName}
                  onChange={updateSignupField('firstName')}
                  autoComplete="given-name"
                  disabled={isSignupSubmitting}
                  required
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  className="auth-input"
                  value={signupForm.lastName}
                  onChange={updateSignupField('lastName')}
                  autoComplete="family-name"
                  disabled={isSignupSubmitting}
                  required
                />
              </div>
              <input
                type="text"
                placeholder="Company"
                className="auth-input"
                value={signupForm.company}
                onChange={updateSignupField('company')}
                autoComplete="organization"
                disabled={isSignupSubmitting}
                required
              />
              <input
                type="email"
                placeholder="Email"
                className="auth-input"
                value={signupForm.email}
                onChange={updateSignupField('email')}
                autoComplete="email"
                disabled={isSignupSubmitting}
                required
              />
              <input
                type="password"
                placeholder="Password"
                className="auth-input"
                value={signupForm.password}
                onChange={updateSignupField('password')}
                autoComplete="new-password"
                disabled={isSignupSubmitting}
                required
              />
              <input
                type="password"
                placeholder="Retype Password"
                className="auth-input"
                value={signupForm.retypePassword}
                onChange={updateSignupField('retypePassword')}
                autoComplete="new-password"
                disabled={isSignupSubmitting}
                required
              />
              <button
                type="submit"
                className="auth-submit-button"
                disabled={isSignupSubmitting}
              >
                {isSignupSubmitting ? (
                  <span className="auth-loading-label">
                    <span className="auth-loading-spinner" />
                    Creating account...
                  </span>
                ) : 'Submit'}
              </button>
            </form>

            <button
              type="button"
              className="auth-secondary-button"
              onClick={handleBackToSignIn}
              disabled={isSignupSubmitting}
            >
              Back to Sign In
            </button>
          </>
        ) : !isResetMode ? (
          <>
            <p className="auth-subtitle">Sign in to continue</p>

            <form onSubmit={handleEmailLogin} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                className="auth-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <input
                type="password"
                placeholder="Password"
                className="auth-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <button
                type="submit"
                className="auth-submit-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Signing in...' : 'Sign in with Email'}
              </button>
            </form>

            <div className="auth-divider">
              <div className="auth-divider-line" />
              <div className="auth-divider-text">or</div>
              <div className="auth-divider-line" />
            </div>

            <button
              type="button"
              className="auth-submit-button auth-signup-button"
              onClick={handleOpenSignup}
              disabled={isSubmitting}
            >
              Create an account
            </button>

            <div id="google-signin-button"></div>
          </>
        ) : (
          <>
            <p className="reset-kicker">Welcome to Trouve</p>
            <h2 className="reset-title">To get started, let&apos;s reset your password.</h2>
            <p className="reset-subtitle">Use a strong password you haven&apos;t used before.</p>

            <form onSubmit={handleResetPassword} className="auth-form">
              <input
                type="email"
                placeholder="Email"
                className="auth-input"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoComplete="email"
                required
              />
              <input
                type="password"
                placeholder="New password"
                className="auth-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <input
                type="password"
                placeholder="Confirm new password"
                className="auth-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                required
              />
              <button
                type="submit"
                className="auth-submit-button"
                disabled={isResetSubmitting}
              >
                {isResetSubmitting ? 'Updating password...' : 'Reset Password'}
              </button>
            </form>

            <button
              type="button"
              className="auth-secondary-button"
              onClick={handleBackToSignIn}
              disabled={isResetSubmitting}
            >
              Back to Sign In
            </button>
          </>
        )}
      </div>
      {(authError || resetError || signupError) && (
        <div className="login-error" role="alert" aria-live="polite">
          {isSignupMode ? signupError : isResetMode ? resetError : authError}
        </div>
      )}
    </div>
  );
}

export default Login;
